import React from "react";
import { createRoot } from "react-dom/client";
import { ChatWidget, ChatWidgetProps } from "./ChatWidget";

// Export component for npm usage
export { ChatWidget };
export type { ChatWidgetProps };

// Auto-initialize for script tag usage
function autoInit() {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[data-widget-id]'
  );

  scripts.forEach((script) => {
    const widgetId = script.getAttribute("data-widget-id");
    if (!widgetId) return;

    // Optional overrides for proxy setups
    const apiBaseUrl = script.getAttribute("data-api-base-url") || undefined;
    const sendUrl = script.getAttribute("data-send-url") || undefined;

    // Optional styling/config overrides
    const position = script.getAttribute("data-position") as "left" | "right" | undefined;
    const headerColor = script.getAttribute("data-header-color") || undefined;
    const companyName = script.getAttribute("data-company-name") || undefined;
    const companyLogo = script.getAttribute("data-company-logo") || undefined;
    const fabLogo = script.getAttribute("data-fab-logo") || undefined;
    const contactEmail = script.getAttribute("data-contact-email") || undefined;
    const welcomeMessage = script.getAttribute("data-welcome-message") || undefined;
    const quickReplies = script.getAttribute("data-quick-replies")?.split(",").map(s => s.trim()).filter(Boolean) || undefined;
    const notificationSound = script.getAttribute("data-notification-sound") || undefined;

    const containerId = `chat-widget-${widgetId}`;
    let container = document.getElementById(containerId);

    if (!container) {
      container = document.createElement("div");
      container.id = containerId;
      document.body.appendChild(container);
    }

    const root = createRoot(container);
    root.render(
      <React.StrictMode>
        <ChatWidget
          widgetId={widgetId}
          apiBaseUrl={apiBaseUrl}
          sendUrl={sendUrl}
          position={position}
          headerColor={headerColor}
          companyName={companyName}
          companyLogo={companyLogo}
          fabLogo={fabLogo}
          contactEmail={contactEmail}
          welcomeMessage={welcomeMessage}
          quickReplies={quickReplies}
          notificationSound={notificationSound}
        />
      </React.StrictMode>
    );
  });
}

// Auto-initialize when DOM is ready
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }
}

// Manual initialization
export function init(config: ChatWidgetProps & { container?: HTMLElement | string }) {
  const { container: containerOption, ...widgetProps } = config;

  let container: HTMLElement;

  if (typeof containerOption === "string") {
    const el = document.getElementById(containerOption);
    if (!el) throw new Error(`Container element not found: ${containerOption}`);
    container = el;
  } else if (containerOption) {
    container = containerOption;
  } else {
    container = document.createElement("div");
    container.id = `chat-widget-${widgetProps.widgetId}`;
    document.body.appendChild(container);
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <ChatWidget {...widgetProps} />
    </React.StrictMode>
  );

  return {
    unmount: () => root.unmount(),
  };
}

// Global namespace
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).LuciusChatWidget = {
    init,
    ChatWidget,
  };
}
